/**
 * Page morphs (#648): the browser's View Transitions API, started by hand.
 *
 * React Router's own `viewTransition` flag is not used: it persists transition state to `sessionStorage` under
 * `remix-router-transitions` (FRONTEND_DESIGN §6.2 forbids browser storage for anything a pathname can carry). This
 * file wraps `router.navigate` instead, which Link, NavLink and useNavigate all call, and stores nothing.
 *
 * A pathname-changing navigation runs inside `document.startViewTransition`. The transition's promise resolves when
 * React has committed the new location (`useMorphCommit`, called by both shells) and any in-flight query has had a
 * short moment to land, so the morph ends on content and not on a skeleton. A hard timeout always resolves it.
 * Rendering is frozen while the promise is pending, so nothing here waits on requestAnimationFrame.
 *
 * The transition is skipped, and navigation proceeds untouched, when the API is missing (jsdom), the person prefers
 * reduced motion, the document is hidden, or only the search or hash changes.
 */

import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router';
import { flushSync } from 'react-dom';

type Direction = 'next' | 'prev' | 'fade' | 'same';

/** The slice of the data router that this file touches. */
export interface MorphRouter {
  navigate(to: unknown, opts?: unknown): Promise<void>;
  readonly state: { readonly location: { readonly pathname: string } };
}

/** The slice of a query client that this file touches. */
export interface MorphQueries {
  isFetching: () => number;
}

interface TransitionLike {
  readonly finished: Promise<unknown>;
  readonly ready: Promise<unknown>;
  skipTransition: () => void;
}

const HARD_TIMEOUT_MS = 250;
const FETCH_WAIT_MS = 120;
const POLL_MS = 20;
const SETTLE_MS = 24;

const TAB_ORDER = ['/', '/homework', '/progress', '/exam-prep'];

let queries: MorphQueries | null = null;
let active: TransitionLike | null = null;
let commitWaiter: (() => void) | null = null;

function canMorph(): boolean {
  if (typeof document === 'undefined' || typeof document.startViewTransition !== 'function') return false;
  if (document.visibilityState === 'hidden') return false;
  if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return false;
  }
  return true;
}

function stripSlash(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/** `next` goes deeper or to a later tab, `prev` the reverse, `fade` is everything else. */
export function morphDirection(from: string, to: string): Direction {
  const a = stripSlash(from);
  const b = stripSlash(to);
  const fi = TAB_ORDER.indexOf(a);
  const ti = TAB_ORDER.indexOf(b);
  if (fi >= 0 && ti >= 0) return ti > fi ? 'next' : 'prev';
  // Settings sits one level above every tab: into it is `next`, back out is `prev`, alike in both directions.
  const inSettings = (p: string): boolean => p === '/settings' || p.startsWith('/settings/');
  if (inSettings(b) && !inSettings(a) && fi >= 0) return 'next';
  if (inSettings(a) && !inSettings(b) && ti >= 0) return 'prev';
  if (b !== '/' && b.startsWith(`${a === '/' ? '' : a}/`)) return 'next';
  if (a !== '/' && a.startsWith(`${b === '/' ? '' : b}/`)) return 'prev';
  if (b.startsWith('/homework/')) return 'next';
  if (a.startsWith('/homework/') && ti >= 0) return 'prev';
  return 'fade';
}

function pathOf(to: unknown, current: string): string | null {
  if (typeof to === 'number') return null;
  let raw: string;
  if (typeof to === 'string') raw = to;
  else if (to !== null && typeof to === 'object' && 'pathname' in to && typeof to.pathname === 'string') raw = to.pathname;
  else return current;
  try {
    return new URL(raw, `http://x${current}`).pathname;
  } catch {
    return null;
  }
}

/**
 * A `viewTransitionName` for a shared element: the same `kind` and `id` on the page that is left and the page that is
 * entered make one element morph into the other. A name used twice in one document silently aborts the whole
 * transition, so put it only where an id appears once per page, never on a list that can repeat an id.
 */
export function morphName(kind: string, id: string): { readonly viewTransitionName: string } {
  return { viewTransitionName: `dv-${kind}-${id.replace(/[^a-zA-Z0-9_-]/g, '_')}` };
}

/** Called by the shells after they commit a new location; resolves the pending transition's commit wait. */
export function notifyMorphCommit(): void {
  const wake = commitWaiter;
  commitWaiter = null;
  wake?.();
}

export function useMorphCommit(): void {
  const location = useLocation();
  useLayoutEffect(() => {
    notifyMorphCommit();
  }, [location.key]);
}

function afterCommit(): Promise<void> {
  return new Promise<void>((resolve) => {
    commitWaiter = () => {
      // Queries start in effects, just after this layout effect; give them a moment to register, then wait for them.
      setTimeout(() => {
        const start = Date.now();
        const poll = (): void => {
          if (queries === null || queries.isFetching() === 0 || Date.now() - start >= FETCH_WAIT_MS) resolve();
          else setTimeout(poll, POLL_MS);
        };
        poll();
      }, SETTLE_MS);
    };
  });
}

function begin(direction: Direction, update: () => void | Promise<unknown>, waitForCommit: boolean): void {
  active?.skipTransition();
  document.documentElement.dataset.morph = direction;
  // The header scrolls away with the page. If it is out of view, its parts must not glide in from above.
  if (window.scrollY > 40) document.documentElement.dataset.morphScrolled = '';
  const commit = waitForCommit ? afterCommit() : Promise.resolve();
  const hard = new Promise<void>((resolve) => setTimeout(resolve, HARD_TIMEOUT_MS));
  const transition = document.startViewTransition(async () => {
    await update();
    await Promise.race([commit, hard]);
  }) as unknown as TransitionLike;
  active = transition;
  transition.ready.catch(() => undefined);
  const done = (): void => {
    if (active === transition) {
      active = null;
      delete document.documentElement.dataset.morph;
      delete document.documentElement.dataset.morphScrolled;
    }
    commitWaiter = null;
  };
  transition.finished.then(done, done);
}

/** Wraps a same-page change (course focus, language, a topic chip) in a soft cross-fade. */
export function withMorph(update: () => void): void {
  if (!canMorph()) {
    update();
    return;
  }
  begin('same', () => flushSync(update), false);
}

export function installPageMorphs(router: MorphRouter, queryClient?: MorphQueries): void {
  queries = queryClient ?? null;
  const navigate = router.navigate.bind(router);
  router.navigate = (to: unknown, opts?: unknown): Promise<void> => {
    const from = router.state.location.pathname;
    const target = pathOf(to, from);
    if (target === null || target === from || !canMorph()) return navigate(to, opts);
    // The caller's promise follows the navigation itself, not the animation.
    const settled = new Promise<void>((resolve, reject) => {
      begin(
        morphDirection(from, target),
        () => {
          const run = navigate(to, opts);
          run.then(resolve, reject);
          return run.catch(() => undefined);
        },
        true,
      );
    });
    return settled;
  };

  if (typeof window === 'undefined') return;
  // Capture phase, so this runs before the router's own popstate listener changes what is on the page.
  window.addEventListener(
    'popstate',
    (event) => {
      if ((event as PopStateEvent & { hasUAVisualTransition?: boolean }).hasUAVisualTransition === true) return;
      const from = router.state.location.pathname;
      const to = window.location.pathname;
      if (from === to || !canMorph()) return;
      begin(morphDirection(from, to), () => undefined, true);
    },
    { capture: true },
  );
}
